import { gql, useQuery } from '@apollo/client';
import { Box, Grid, GridItem } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { MembershipCtaCard } from '@/components/MembershipCtaCard';
import { BlogDetail, Post, QuestionDetail } from '@/components/Post';
import { usePermissions } from '@/hooks/usePermissions';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { useAutoLikeComment } from '@/imports/ui/hooks/useAutoLikeComment';
import { useAutoLikePost } from '@/imports/ui/hooks/useAutoLikePost';
import { useAutoPollVote } from '@/imports/ui/hooks/useAutoPollVote';
import { Butterfly, ButterflyMain } from '@/layouts/Butterfly';

import {
  NonCommunityPostPage_GetPost,
  NonCommunityPostPage_GetPostVariables,
  NonCommunityPostPage_GetPost_postV2_result,
} from './graphql-types/NonCommunityPostPage_GetPost';

import { PostRemovedCard } from './components/PostRemovedCard';
import { SeeMoreButtons } from './components/SeeMoreButtons';

const GET_POST = gql`
  query NonCommunityPostPage_GetPost($input: SinglePostInputV2!) {
    postV2(input: $input) {
      result {
        _id
        group {
          _id
        }
        ...Post_PostV2Fragment
        ...BlogDetail_BlogFragment
        ...QuestionDetail_QuestionV2Fragment
        ...SeeMoreButtons_PostV2Fragment
      }
    }
    currentUser {
      _id
      firstName
      membershipData {
        ...MembershipCtaCard_MembershipDataFragment
      }
    }
  }
  ${Post.postFragment}
  ${BlogDetail.blogFragment}
  ${QuestionDetail.questionFragment}
  ${SeeMoreButtons.postFragment}
  ${MembershipCtaCard.membershipDataFragment}
`;

const MainContent: React.FC<{
  post: NonCommunityPostPage_GetPost_postV2_result;
}> = ({ post }) => {
  useAutoPollVote(VALID_ROUTES);
  useAutoLikeComment(VALID_ROUTES);
  useAutoLikePost(VALID_ROUTES);

  return post.__typename === 'BlogV2' ? (
    <BlogDetail blog={post} options={{ isExpandedByDefault: true }} />
  ) : post.__typename === 'QuestionV2' ? (
    <QuestionDetail question={post} options={{ isExpandedByDefault: true }} />
  ) : (
    <Post post={post} options={{ isExpandedByDefault: true }} />
  );
};

const VALID_ROUTES = {
  validRoutes: ['posts'],
};

export const NonCommunityPostPage: React.FC = () => {
  const router = useRouter();

  const permissions = usePermissions();

  const query = useQuery<
    NonCommunityPostPage_GetPost,
    NonCommunityPostPage_GetPostVariables
  >(GET_POST, { variables: { input: { id: router.query.postId.toString() } } });

  // if it has a group then it is part of the community so we redirect
  const shouldRedirect =
    query.data?.postV2.result && query.data.postV2.result.group;

  React.useEffect(() => {
    if (
      !permissions.isLoading &&
      !query.loading &&
      !permissions.isAuthenticated &&
      (!query.data || !query.data.postV2.result || query.error)
    ) {
      router.push({
        pathname: `/login`,
        query: {
          redirect: router.asPath,
        },
      });
    } else if (shouldRedirect) {
      router.replace({
        pathname: '/community/post/[postId]',
        query: router.query,
      });
    }
  }, [
    permissions.isLoading,
    query.loading,
    router,
    permissions.isAuthenticated,
    query.data,
    query.error,
    shouldRedirect,
  ]);

  return (
    <Butterfly>
      <ButterflyMain>
        <Box maxW="container.xl" mx="auto" p="4">
          <Grid templateColumns="repeat(12, 1fr)" gap="4">
            <GridItem
              colSpan={{ base: 12, lg: 3 }}
              display={{ base: 'none', lg: 'block' }}
            >
              {/* empty */}
            </GridItem>

            {query.loading || shouldRedirect ? (
              <>
                <GridItem colSpan={{ base: 12, md: 8, lg: 6 }}>
                  <Loading />
                </GridItem>
                <GridItem
                  colSpan={{ base: 12, md: 4, lg: 3 }}
                  display={{ base: 'none', md: 'block' }}
                />
              </>
            ) : !query.data || !query.data.postV2.result || query.error ? (
              <>
                <GridItem colSpan={{ base: 12, md: 8, lg: 6 }}>
                  <PostRemovedCard />
                </GridItem>
                <GridItem
                  colSpan={{ base: 12, md: 4, lg: 3 }}
                  display={{ base: 'none', md: 'block' }}
                />
              </>
            ) : (
              <>
                <GridItem colSpan={{ base: 12, md: 8, lg: 6 }}>
                  <MainContent post={query.data.postV2.result} />
                </GridItem>
                <GridItem
                  colSpan={{ base: 12, md: 4, lg: 3 }}
                  display={{ base: 'none', md: 'block' }}
                >
                  {query.data?.currentUser?.membershipData && (
                    <MembershipCtaCard
                      membershipData={query.data.currentUser.membershipData}
                      userFirstName={query.data.currentUser.firstName}
                      shouldBeMemberView
                    />
                  )}
                </GridItem>
              </>
            )}
          </Grid>
        </Box>
      </ButterflyMain>
    </Butterfly>
  );
};
